// 周期配置（聚合规则、样式、MA周期）
export const PERIOD_CONFIG = {
    m1: {
        name: 'm1',
        code: 'm1',
        upColor: '#00da3c', // 阳线颜色（上涨）
        downColor: '#ec0000', // 阴线颜色（下跌）
        lineColors: ['#ECD083', '#FC4C34', '#2583FB', '#773BF3'],
        maPeriods: [5, 10, 20, 30],
        aggregateMin: 1
    },
    m5: {
        name: 'm5',
        code: 'm5',
        upColor: '#00da3c',
        downColor: '#ec0000',
        lineColors: ['#ECD083', '#FC4C34', '#2583FB', '#773BF3'],
        maPeriods: [3, 6, 12, 24],
        aggregateMin: 5
    },
    m15: {
        name: 'm15',
        code: 'm15',
        upColor: '#00da3c',
        downColor: '#ec0000',
        lineColors: ['#ECD083', '#FC4C34', '#2583FB', '#773BF3'],
        maPeriods: [2, 4, 8, 16],
        aggregateMin: 15
    },
    m30: {
        name: 'm30',
        code: 'm30',
        upColor: '#00da3c',
        downColor: '#ec0000',
        lineColors: ['#ECD083', '#FC4C34', '#2583FB', '#773BF3'],
        maPeriods: [1, 2, 4, 8],
        aggregateMin: 30
    },
    h1: {
        name: 'h1',
        code: 'h1',
        upColor: '#00da3c',
        downColor: '#ec0000',
        lineColors: ['#ECD083', '#FC4C34', '#2583FB', '#773BF3'],
        maPeriods: [1, 2, 4, 8],
        aggregateMin: 60
    }
};


// ================= 核心工具函数 =================
/**
 * 格式化秒级时间为聚合分组键（按分钟向下取整）
 * @param {string} timeStr - 原始时间字符串（如"2025/11/10 21:57:33"）
 * @param {number} aggregateMin - 聚合分钟数
 * @returns {string} 分组时间键（如"2025/11/10 21:57"）
 */
export function formatGroupKey(timeStr, aggregateMin) {
    const trimmedTime = timeStr.trim().replace(/\s+/g, ' ');
    const date = new Date(trimmedTime.replace(/\//g, '-'));
    
    if (isNaN(date.getTime())) {
        throw new Error(`时间格式解析失败：${timeStr}（请确保格式为"YYYY/MM/DD HH:MM:SS"）`);
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    let minutes = date.getMinutes();
    
    if (aggregateMin > 1) {
        minutes = Math.floor(minutes / aggregateMin) * aggregateMin;
    }
    const minutesStr = String(minutes).padStart(2, '0');
    
    return `${year}/${month}/${day} ${hours}:${minutesStr}`;
}

/**
 * 聚合秒级数据为目标周期K线
 * @param {array} data - 原始秒级数据（[时间, 价格, 成交量]）
 * @param {number} aggregateMin - 聚合分钟数
 * @returns {array} 聚合后K线数据（[时间, 开盘, 收盘, 最高, 最低, 成交量, 涨跌额, 涨跌幅]）
 */
export function aggregateToKLine(data, aggregateMin) {
    const groupMap = new Map();
    data.forEach(item => {
        try {
            const groupKey = formatGroupKey(item[0], aggregateMin);
            if (!groupMap.has(groupKey)) {
                groupMap.set(groupKey, []);
            }
            groupMap.get(groupKey).push(item);
        } catch (error) {
            console.warn(`跳过无效数据：${item}，原因：${error.message}`);
        }
    });
    
    const groupKeys = Array.from(groupMap.keys()).sort((a, b) => new Date(a) - new Date(b));
    const kLineData = [];
    
    groupKeys.forEach((key, index) => {
        const groupItems = groupMap.get(key);
        const prices = groupItems.map(item => parseFloat(item[1])).filter(val => !isNaN(val));
        const volumes = groupItems.map(item => parseFloat(item[2])).filter(val => !isNaN(val));
        
        if (prices.length === 0 || volumes.length === 0) {
            console.warn(`跳过空分组：${key}`);
            return;
        }
        
        const open = prices[0].toFixed(2); // 开盘=组内第一笔价格
        const close = prices[prices.length - 1].toFixed(2); // 收盘=组内最后一笔价格
        const high = Math.max(...prices).toFixed(2); // 最高=组内最高价格
        const low = Math.min(...prices).toFixed(2); // 最低=组内最低价格
        const totalVolume = Math.sum(...volumes).toFixed(0); // 成交量=组内总和
        
        // let change = '0.00';
        // let changeRate = '0.00%';
        // if (index > 0 && kLineData.length > 0) {
        //     const prevClose = parseFloat(kLineData[index - 1][2]);
        //     change = (parseFloat(close) - prevClose).toFixed(2);
        //     changeRate = ((parseFloat(change) / prevClose) * 100).toFixed(2) + '%';
        // }
        let change=(high-low).toFixed(2);
        let changeRate=((parseFloat(change)/low )*100).toFixed(2)+"%"
        
        kLineData.push([
            key, open, close, high, low, totalVolume, change, changeRate
        ]);
    });
    
    return kLineData;
}

// 扩展Math.sum方法（计算数组总和）
Math.sum = function(...args) {
    if (args.length === 1 && Array.isArray(args[0])) {
        return args[0].reduce((acc, val) => acc + (isNaN(val) ? 0 : val), 0);
    }
    return args.reduce((acc, val) => acc + (isNaN(val) ? 0 : val), 0);
};

/**
 * 计算MA均线
 * @param {array} kLineData - K线数据
 * @param {number} period - MA周期（K线根数）
 * @returns {array} MA值数组
 */
export function calculateMA(kLineData, period) {
    const maData = [];
    for (let i = 0; i < kLineData.length; i++) {
        if (i < period - 1) {
            maData.push('-');
            continue;
        }
        let sum = 0;
        for (let j = 0; j < period; j++) {
            sum += parseFloat(kLineData[i - j][2]);
        }
        maData.push((sum / period).toFixed(2));
    }
    return maData;
}

/**
 * 格式化图表数据（关键修复：K线数据顺序）
 * @param {array} kLineData - K线数据
 * @returns {object} 图表所需数据
 */
export function formatChartData(kLineData) {
    const dates = [];
    const candlestickData = []; // K线数据（格式：[开盘, 最高, 最低, 收盘]）
    const volumeData = [];
    const rawMap = new Map();
    
    kLineData.forEach(item => {
        const [time, open, close, high, low, volume, change, changeRate] = item;
        dates.push(time);
        // 关键修复：ECharts K线图必须遵循 [开盘价, 最高价, 最低价, 收盘价] 顺序
        candlestickData.push([
            parseFloat(open),
            parseFloat(high),
            parseFloat(low),
            parseFloat(close)
        ]);
        volumeData.push(parseFloat(volume));
        rawMap.set(time, {
            open, close, high, low, volume, change, changeRate
        });
    });
    
    return { dates, candlestickData, volumeData, rawMap };
}